export type PostLoginPanelTab = 'SUMMARY' | 'SECTOR' | 'STOCK' | 'HOLDING' | 'ALERT';

export type AuthIntent = {
  returnTo: string;
  openPanel: boolean;
  panelTab: PostLoginPanelTab;
  reason: 'manual' | 'protected-action' | 'session-expired' | 'social-login';
  createdAt: number;
};

const AUTH_INTENT_KEY = 'policy-intel:auth-intent';
const LOGIN_COMPLETED_KEY = 'policy-intel:login-completed';
const INTENT_MAX_AGE_MS = 30 * 60 * 1000;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

export function saveAuthIntent(
    input: Partial<AuthIntent> & Pick<AuthIntent, 'returnTo'>,
): AuthIntent | null {
  if (!isBrowser()) return null;

  const intent: AuthIntent = {
    returnTo: input.returnTo || '/',
    openPanel: input.openPanel ?? true,
    panelTab: input.panelTab ?? 'SUMMARY',
    reason: input.reason ?? 'manual',
    createdAt: Date.now(),
  };

  localStorage.setItem(AUTH_INTENT_KEY, JSON.stringify(intent));
  return intent;
}

export function peekAuthIntent(): AuthIntent | null {
  if (!isBrowser()) return null;

  try {
    const raw = localStorage.getItem(AUTH_INTENT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<AuthIntent>;
    const createdAt = Number(parsed.createdAt || 0);

    if (!createdAt || Date.now() - createdAt > INTENT_MAX_AGE_MS) {
      localStorage.removeItem(AUTH_INTENT_KEY);
      return null;
    }

    return {
      returnTo: String(parsed.returnTo || '/'),
      openPanel: parsed.openPanel !== false,
      panelTab: (parsed.panelTab || 'SUMMARY') as PostLoginPanelTab,
      reason: (parsed.reason || 'manual') as AuthIntent['reason'],
      createdAt,
    };
  } catch {
    localStorage.removeItem(AUTH_INTENT_KEY);
    return null;
  }
}

export function consumeAuthIntent(): AuthIntent | null {
  const intent = peekAuthIntent();
  if (isBrowser()) localStorage.removeItem(AUTH_INTENT_KEY);
  return intent;
}

export function clearAuthIntent(): void {
  if (!isBrowser()) return;
  localStorage.removeItem(AUTH_INTENT_KEY);
}

export function markLoginCompleted(method: 'password' | 'social' | 'restored'): void {
  if (!isBrowser()) return;
  localStorage.setItem(
      LOGIN_COMPLETED_KEY,
      JSON.stringify({method, completedAt: Date.now()}),
  );
}

export function consumeRecentLoginCompleted(maxAgeMs = 60_000): boolean {
  if (!isBrowser()) return false;

  try {
    const raw = localStorage.getItem(LOGIN_COMPLETED_KEY);
    localStorage.removeItem(LOGIN_COMPLETED_KEY);
    if (!raw) return false;

    const parsed = JSON.parse(raw) as { completedAt?: number };
    const completedAt = Number(parsed.completedAt || 0);
    return completedAt > 0 && Date.now() - completedAt <= maxAgeMs;
  } catch {
    localStorage.removeItem(LOGIN_COMPLETED_KEY);
    return false;
  }
}
